import { DashboardCard } from "@/core/components/ui/dashboard-card";
import { CircularProgress } from "@/core/components/ui/circular-progress";
import { useTranslation } from "react-i18next";
import { useCheckInRate } from "../hooks/use-check-in-rate";
import { useMemo } from "react";
import { truncateToTwoDecimals } from "@/_old/utils/number";
import { DashboardEmptyState } from "../../dashboard-empty-state";

interface CheckInRateCardProps {
  startDate: string;
  endDate: string;
  companyId: number;
  cities?: string[] | null;
  spaces?: number[] | null;
  users?: number[] | null;
  status?: number[] | null;
  types?: string[] | null;
  group_id?: number | null;
}

export function CheckInRateCard({
  startDate,
  endDate,
  companyId,
  cities,
  spaces,
  users,
  status,
  types,
  group_id,
}: CheckInRateCardProps) {
  const [t] = useTranslation(["dashboard"]);

  const { data, isLoading } = useCheckInRate({
    startDate,
    endDate,
    companyId,
    cities,
    spaces,
    users,
    status,
    types,
    group_id,
  });

  const verifyEmptyData = useMemo(() => {
    return !isLoading && (!data || data?.percentage === 0);
  }, [data, isLoading]);

  const usageStatus = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    label: string;
    color: string;
  } => {
    if (!data?.percentage || data?.percentage === 0) {
      return {
        type: "default",
        label: t("dashboard-check-in-no-usage", "Sem uso"),
        color: "text-gray-600",
      };
    }

    if (data.percentage > 90) {
      return {
        type: "good",
        label: t("dashboard-check-in-good-usage", "Bom uso"),
        color: "text-teal-600",
      };
    }

    if (data.percentage >= 80) {
      return {
        type: "warning",
        label: t("dashboard-check-in-moderate-usage", "Uso moderado"),
        color: "text-amber-600",
      };
    }

    return {
      type: "danger",
      label: t("dashboard-check-in-low-usage", "Baixo uso"),
      color: "text-red-600",
    };
  }, [data, t]);

  if (isLoading) {
    return (
      <DashboardCard
        className="bg-card border border-border shadow-sm"
        title={t("dashboard-check-in-rate", "Taxa de check-in")}
      >
        <div className="flex flex-col gap-6 justify-center items-center">
          <div className="w-36 h-36 bg-gray-200 rounded-full animate-pulse" />
          <div className="mx-auto w-16 h-4 bg-gray-200 rounded animate-pulse" />
        </div>
      </DashboardCard>
    );
  }

  if (verifyEmptyData) {
    return (
      <DashboardCard
        className="bg-card border border-border shadow-sm"
        title={t("dashboard-check-in-rate", "Taxa de check-in")}
      >
        <div className="flex items-center justify-center h-[200px]">
          <DashboardEmptyState />
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      className="bg-card border border-border shadow-sm"
      title={t("dashboard-check-in-rate", "Taxa de check-in")}
    >
      <div className="flex flex-col gap-6 justify-center items-center">
        <CircularProgress
          value={data?.percentage ? truncateToTwoDecimals(data.percentage) : 0}
          color={usageStatus.color}
          label="Check-in"
        />
        <p className={`text-sm ${usageStatus.color}`}>{usageStatus.label}</p>
      </div>
    </DashboardCard>
  );
}
